// SolarTimes.js — GÜN DOĞUMU / BATIMI ve GECE (4 Eyl 2026)
//
// 🔑 TANIM TALİMATTAN — SHT-FCL Md.4(r), kelimesi kelimesine:
//    *"Gece: Akşam alacakaranlığın sonu (günbatımı+30 dk.) ve sabah
//     alacakaranlığın başlangıcı (gündoğumu−30 dk.) arasındaki süreyi"*
//
// ⚠️ Bu ölçülerek doğrulandı, varsayılmadı. ICAO Annex 15 (APIRG/18 IP/4
//    App.B) AIP GEN 2.7 tablolarının SİVİL ALACAKARANLIK saatlerini taşımasını
//    ister; sivil alacakaranlık aslında güneşin ufkun 6° altında olduğu andır
//    ve enlem/mevsimle DEĞİŞİR. Ama bizi bağlayan metin SHT-FCL'dir ve o,
//    alacakaranlığı 30 DAKİKA OLARAK SABİTLEMİŞTİR. Bu yüzden −6° geometrisi
//    değil, talimatın sabit 30 dakikası uygulanır. Aynı not: ICAO'nun kendisi
//    de tablo yerine formül kullanmayı açıkça kabul eder (GEN 2.7 notu).
//
// 🔑 Serkan: *"Gündoğumu günbatımı −/+ 30dk."* · *"Bir meydanlar listemiz ve
//    koordinatları olan file'ımız var — ordan biz gece gündüz çıkartabiliriz."*
//
// 🔴 NEDEN HESAPLIYORUZ, SAKLAMIYORUZ: `archived_flights.is_night_landing`
//    saklanmak üzere kondu ama `archive-flight` her kayda sabit `false` yazıyor
//    (satır 373, yanında "hesabı ileride buna dayanır" notuyla). Sonuç:
//    STATISTICS'teki "Night Landings" bugüne kadar HER ZAMAN 0 gösterdi —
//    ölçmediği bir şeyi ölçmüş gibi (İlke 1). Hesaplanan değer bayatlamaz ve
//    GEÇMİŞE DÖNÜK çalışır: eski uçuşlar için de bugün doğru cevabı verir.
//
// Yöntem: NOAA güneş konumu algoritması. Dışarıdan servis YOK — koordinat ve
// tarih yeter. Ufuk açısı −0.833° (kırılma + güneş yarıçapı), yani "resmî"
// doğuş/batış.
//
// ⚠️ KUTUP DURUMU: güneşin hiç doğmadığı/batmadığı yerde doğuş/batış YOKTUR.
//    Uydurma saat döndürmeyiz; `null` döner ve çağıran "ölçülemedi" der.

const D2R = Math.PI / 180, R2D = 180 / Math.PI;
const UFUK_DERECE = -0.833;

/** Tarihten (UTC) Julian gün — 2000.0 epoğuna göre. */
function julianGun(ms) { return ms / 86400000 + 2440587.5; }

/**
 * Bir meydan ve gün için doğuş/batış anları (UTC, ms).
 * @param {number} lat enlem (kuzey +)
 * @param {number} lon boylam (doğu +)
 * @param {string|number|Date} gun o güne ait herhangi bir an
 * @returns {{sunrise:number|null, sunset:number|null, polar:'day'|'night'|null}}
 */
export function gunesOlaylari(lat, lon, gun) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { sunrise:null, sunset:null, polar:null };
  const t = new Date(gun);
  if (isNaN(t)) return { sunrise:null, sunset:null, polar:null };

  // O günün UTC gece yarısı — olaylar bu güne ait sayılır.
  const geceYarisi = Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
  const n = Math.round(julianGun(geceYarisi) - 2451545.0 + 0.0008);

  const ortalamaSolarNoon = n - lon / 360;
  const M = (357.5291 + 0.98560028 * ortalamaSolarNoon) % 360;          // ortalama anomali
  const C = 1.9148 * Math.sin(M * D2R) + 0.0200 * Math.sin(2 * M * D2R)
          + 0.0003 * Math.sin(3 * M * D2R);                              // merkez denklemi
  const L = (M + C + 180 + 102.9372) % 360;                              // ekliptik boylam
  const transit = 2451545.0 + ortalamaSolarNoon + 0.0053 * Math.sin(M * D2R)
                - 0.0069 * Math.sin(2 * L * D2R);                        // güneş öğlesi (JD)
  const dekl = Math.asin(Math.sin(L * D2R) * Math.sin(23.4397 * D2R));   // deklinasyon

  const cosH = (Math.sin(UFUK_DERECE * D2R) - Math.sin(lat * D2R) * Math.sin(dekl))
             / (Math.cos(lat * D2R) * Math.cos(dekl));
  const jdMs = (jd) => (jd - 2440587.5) * 86400000;
  if (cosH > 1) return { sunrise:null, sunset:null, polar:'night' };     // güneş hiç doğmuyor
  if (cosH < -1) return { sunrise:null, sunset:null, polar:'day' };      // güneş hiç batmıyor

  const H = Math.acos(cosH) * R2D;
  const batis = transit + H / 360;
  const dogus = transit - H / 360;
  return { sunrise: jdMs(dogus), sunset: jdMs(batis), polar: null };
}

/** Havacılık gecesi: batış +margin → doğuş −margin (Serkan: ∓30 dk). */
export const GECE_MARJI_DK = 30;

/**
 * Verilen an o meydanda GECE mi?
 * @returns {boolean|null} null = ÖLÇÜLEMEDİ (koordinat yok veya kutup günü/gecesi)
 */
export function geceMi(lat, lon, an, marjDk = GECE_MARJI_DK) {
  const ms = new Date(an).getTime();
  if (isNaN(ms)) return null;
  const marj = marjDk * 60000;

  // Gün sınırını geçen anlar için önceki/o gün/sonraki güne bakılır: gece
  // yarısı civarındaki bir iniş, DÜNKÜ batıştan sonra ve BUGÜNKÜ doğuştan
  // öncedir. Tek güne bakmak gece yarısını gündüz sayardı.
  for (const kaydir of [-1, 0]) {
    const g = gunesOlaylari(lat, lon, ms + kaydir * 86400000);
    if (g.polar === 'night') return true;
    if (g.polar === 'day') return false;
    if (g.sunrise == null || g.sunset == null) return null;

    const sonraki = gunesOlaylari(lat, lon, ms + (kaydir + 1) * 86400000);
    if (kaydir === 0) {
      // Bugünün doğuşundan önce mi? (dünkü batıştan sonrasıysa gece)
      if (ms < g.sunrise - marj) {
        const dun = gunesOlaylari(lat, lon, ms - 86400000);
        if (dun.polar === 'night') return true;
        if (dun.polar === 'day') return false;
        if (dun.sunset == null) return null;
        return ms > dun.sunset + marj;
      }
      if (ms > g.sunset + marj) {
        if (sonraki.polar === 'night') return true;
        if (sonraki.polar === 'day') return false;
        if (sonraki.sunrise == null) return null;
        return ms < sonraki.sunrise - marj;
      }
      return false;                       // doğuş−marj ile batış+marj arası = gündüz
    }
  }
  return null;
}


// ── BEYAN EDİLEN MEYDANIN GECESİ (21 Eyl 2026) ───────────────────────
//
// 🔑 Serkan: *"Meydanın UTC saatini yazar (yaz veya kış neyse onu bu işi yapan
//    kişi yapar, SORUMLULUK ONDADIR), meydan sunset ve sunrise saatlerini de
//    yazar."*  · *"Bu meydan database'e girmez, bir kereye mahsus."*
//
// `airports` tablosunda olmayan bir meydanın KOORDİNATI yoktur, dolayısıyla
// `gunesOlaylari` hesap yapamaz. Girdi hesaplanmaz, BEYAN EDİLİR — çıkan karar
// yine aynı kuraldır.
//
// 🔑 GECE TANIMI TEK: SHT-FCL Md.4(r) batış+30 → doğuş−30. Marj `GECE_MARJI_DK`
//    sabitinden okunur, burada İKİNCİ BİR SAYI YAZILMAZ (İlke 3). Değişen tek
//    şey olayların NEREDEN geldiği: koordinattan hesap ↔ kullanıcı beyanı.
//
// ⚠️ `geceMi`nin gün kaydırmalı döngüsü BİLEREK kullanılmadı. O döngü mutlak
//    zaman damgalarıyla çalışır ve "doğuş batıştan önce gelir" varsayar; beyan
//    ise HER GÜN AYNI duvar saatidir ve gündüz penceresi UTC gece yarısını
//    AŞABİLİR (ör. +12 dilimindeki bir meydanda doğuş 20:00Z, batış 08:00Z).
//    Böyle bir girdide o döngü sessizce yanlış cevap verirdi. Burada gün sınırı
//    diye bir kavram yok: pencere dairesel aritmetikle ölçülür — kaba ama her
//    koşulda çalışır (İlke 9).
const HHMM_DK = (s) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(s ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Beyan edilen doğuş/batışa göre verilen an GECE mi?
 * @param {string|null} sunriseUTC "HH:MM" (UTC) — kutup gününde/gecesinde null
 * @param {string|null} sunsetUTC  "HH:MM" (UTC)
 * @returns {boolean|null} null = ÖLÇÜLEMEDİ (beyan eksik/bozuk)
 */
export function geceMiBeyan(sunriseUTC, sunsetUTC, an, marjDk = GECE_MARJI_DK) {
  const dogus = HHMM_DK(sunriseUTC), batis = HHMM_DK(sunsetUTC);
  // Kutup günü/gecesi `null` beyan edilir — uydurma saat yazılmaz, cevap da
  // uydurulmaz. "Ölçemedim" ile "gündüzdü" ayrı şeylerdir (İlke 1).
  if (dogus == null || batis == null) return null;
  const ms = new Date(an).getTime();
  if (isNaN(ms)) return null;

  const gunduzDk = (batis - dogus + 1440) % 1440;   // doğuştan batışa
  const pencere = gunduzDk + 2 * marjDk;            // marjlarla birlikte
  if (pencere >= 1440) return false;                // pencere günü doldurur

  const d = new Date(ms);
  const anDk = d.getUTCHours() * 60 + d.getUTCMinutes();
  const bas = ((dogus - marjDk) % 1440 + 1440) % 1440;
  return ((anDk - bas) % 1440 + 1440) % 1440 > pencere;
}

/**
 * MEYDANIN GECESİ — TEK GİRİŞ NOKTASI (21 Eyl 2026).
 *
 * Gece/gündüz üç yerde ölçülüyordu (`FTLPanel` · `FTLEngine.pfSectors` ·
 * `archive-flight`) ve üçü de aynı deseni kendi içinde kuruyordu: koordinatı
 * bul, yoksa `null` dön. Tanımsız meydan beyanı gelince üçüne birden ikinci bir
 * dal eklemek gerekti — o dalı üç kez yazmak yerine soru tek yere taşındı.
 *
 * SIRA ÖNEMLİ: önce KOORDİNAT (gerçek meydan, hesaplanır), sonra BEYAN.
 * Beyan yalnızca `airports`ta olmayan meydan içindir; olan bir meydanın
 * hesabını beyanla ezmek, ölçülebilen bir şeyi elle yazmak olurdu.
 *
 * @param {string} icao
 * @param {string|number|Date} an
 * @param {{geo?:Object, beyan?:Object}} kaynak
 *        geo:   ICAO → {lat, lon}          (`airports`)
 *        beyan: ICAO → {sunrise_utc, sunset_utc}  (`crew_duties.ad_hoc_airports`)
 * @returns {boolean|null} null = ÖLÇÜLEMEDİ
 */
export function geceMiMeydan(icao, an, { geo = null, beyan = null } = {}) {
  const k = String(icao ?? '').toUpperCase();
  if (!k || !an) return null;
  const g = geo && geo[k];
  if (g) {
    const la = Number(g.lat), lo = Number(g.lon);
    if (Number.isFinite(la) && Number.isFinite(lo)) return geceMi(la, lo, an);
  }
  const b = beyan && beyan[k];
  if (b) return geceMiBeyan(b.sunrise_utc, b.sunset_utc, an);
  return null;
}

// ── SEKTÖRÜN GECE İŞARETLERİ (7 Eyl 2026) ────────────────────────────
//
// 🔑 Serkan: *"Paper OFP girişlerde night/day hesabı yapabiliyor muyuz?
//    Meydanlar ve saatler belli çünkü."*  → Evet, yapabiliyoruz.
//
// 🔴 NEDEN ÖNCE `null` DÖNÜYORDU: bu hesap arşiv akışında SUNUCUDA koşuyor
//    (`archive-flight` Edge Function). Elle giriş yolunda istemciye hiç
//    bağlanmamıştı — yani ilke değil, EKSİKLİKTİ. Ölçülebilir bir şeyi
//    "ölçülemedi" diye bırakmak da bir tür yanlış beyandır.
//
// 🔑 SUNUCUYLA AYNI HESAP: `geceMi` zaten ortak; burada yalnız aynı sarmalayıcı
//    kuruluyor (`archive-flight/index.ts` içindeki `geceIsareti` + `ts`).
//    İki ayrı gece tanımı olsaydı aynı uçuş, arşivden mi elle mi girildiğine
//    göre farklı sonuç verirdi.
//
// Koordinat ya da saat yoksa `null` döner — `false` DEĞİL. `false` "gündüzdü"
// demektir ve bunu ölçmeden söyleyemeyiz (İlke 1).

// 🗑️ `utcAn` da SİLİNDİ (21 Eyl 2026, Serkan onayı) — `geceIsaretleri` ile
// birlikte. Üretimdeki tek çağıranı oydu; o gidince sıfır çağıranı kaldı.
// Üstelik `FTLEngine.ts()` ile AYNI fonksiyondu, yalnız argümanları ters:
// `ts(hhmm, isoDate)` ↔ `utcAn(isoTarih, hhmm)`. Aynı işi yapan, biri ölü,
// argümanı ters iki ikiz — er ya da geç biri yanlışını çağırırdı (İlke 3).
// Kenar durumları `supabase/functions/archive-flight/zaman.test.ts`te `ts()`
// üzerinden korunuyor.

// 🗑️ `geceIsaretleri` SİLİNDİ (21 Eyl 2026, Serkan onayı).
//
// Üretimde çağıranı KALMAMIŞTI (ölçüldü: web · Edge · iOS = sıfır); kâğıt OFP
// yazıcısı artık `geceMi`yi TAM ZAMAN DAMGASIYLA doğrudan çağırıyor. Tutmanın
// tek etkisi risk olurdu: imzası `duty_date` + `HH:MM` idi ve KENDİ gün devri
// kuralını içinde taşıyordu — yani 15 Eylül'de FDP'yi **−19:25** gösteren
// kusurun tam şekli. Kanonik gün devri `FTLEngine.sektorZamanlari`; gece kuralı
// (batış+30 → doğuş−30) `geceMi`de ve kendi testleriyle korunuyor.
// 🔑 Serkan: gece işareti, koordinatı olmayan (elle eklenen) meydanlarda zaten
//    ELLE giriliyor — çok nadir bir hâl.
